import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import { unpack, pack } from 'msgpackr';
import stringify from '../utils/stableStringify';
import { Popup } from 'devextreme-react/popup';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { Button } from 'devextreme-react/button';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import AntErrorPanel from './AntErrorPanel';
import AntLocalization from '../utils/AntLocalization';

export default forwardRef(function (props, ref) {
    const popup = useRef();

    const getDefaultWidgets = useCallback(() => {
        var ar = [];
        if (props.widgets && props.widgets instanceof Array) {
            for (let i = 0; i < props.widgets.length; i++) {
                const widget = props.widgets[i];
                ar.push({
                    index: i,
                    ...widget
                });
            }
        }
        return ar;
    }, [props.widgets]);

    // Some callers create their widgets inline on every render. Keep an immutable
    // signature so synchronizing popup widgets does not recursively re-render the
    // DevExtreme wrapper.
    const widgetsSignature = stringify(props.widgets instanceof Array ? props.widgets : []);

    const [mainState, setMainState] = useState({
        popupVisible: false,
        popupHeight: props.height ? props.height : 200,
        popupWidth: props.width ? props.width : 200,
        popupWidgets: getDefaultWidgets(),
        processingIndex: -1
    });

    const onCustomMainState = useCallback((data, changeData) => {
        return data;
    }, []);

    const updateState = useCallback(
        (data) => {
            setMainState((current) => onCustomMainState({ ...current, ...data }, data) || current);
        },
        [onCustomMainState]
    );

    const forceRenderWithState = useCallback((data) => {
        updateState(data);
    }, [updateState]);

    const isShowing = useCallback(() => {
        return mainState.popupVisible;
    }, [mainState]);

    const show = useCallback(
        async (cb, prepare) => {
            if (prepare) await prepare();

            setTimeout(async () => {
                if (cb) await cb();

                forceRenderWithState({
                    popupVisible: true
                });
            }, 200);
        },
        [forceRenderWithState]
    );

    const close = useCallback(() => {
        forceRenderWithState({
            popupVisible: false
        });
        if (props.onClosed) setTimeout(props.onClosed, 5000);
    }, [forceRenderWithState, props.onClosed]);

    const onShowing = useCallback(() => {
        if (props.onShowing) props.onShowing();
    }, [props.onShowing]);

    const onShown = useCallback(() => {
        if (props.onShown) props.onShown();
    }, [props.onShown]);

    const setActionProgress = useCallback(
        (e, status, onFinished) => {
            if (!e.element.id || String(e.element.id).indexOf('ant-popup-button-id-') <= -1) return;

            var index = String(e.element.id).replace('ant-popup-button-id-', '');
            index = parseInt(index);

            let widgets = JSON.parse(stringify(mainState.popupWidgets));
            let widget = widgets[index];

            if (widget) {
                forceRenderWithState({
                    processingIndex: status ? index : -1
                });

                if (onFinished) onFinished(widget);
            }
        },
        [mainState, forceRenderWithState]
    );

    const onAction = useCallback(
        (e) => {
            if (mainState.processingIndex >= 0) return;

            setActionProgress(e, true, async (widget) => {
                if (props.onAction) {
                    var shouldClose = await props.onAction(widget);
                    if (shouldClose) close();
                } else {
                    close();
                }

                setActionProgress(e, false);
            });
        },
        [mainState, props.onAction, close]
    );

    const setHeight = useCallback(
        (height) => {
            forceRenderWithState({
                popupHeight: height
            });
        },
        [forceRenderWithState]
    );

    const setWidth = useCallback(
        (width) => {
            forceRenderWithState({
                popupWidth: width
            });
        },
        [forceRenderWithState]
    );

    const setSize = useCallback(
        (height, width) => {
            forceRenderWithState({
                popupHeight: height ? height : mainState.popupHeight,
                popupWidth: width ? width : mainState.popupWidth
            });
        },
        [mainState, forceRenderWithState]
    );

    useEffect(() => {
        const popupWidgets = getDefaultWidgets();
        setMainState((current) => {
            const popupHeight = props.height ? props.height : current.popupHeight;
            const popupWidth = props.width ? props.width : current.popupWidth;
            const widgetsChanged = stringify(current.popupWidgets) !== stringify(popupWidgets);
            if (!widgetsChanged && current.popupHeight === popupHeight && current.popupWidth === popupWidth) return current;
            return {
                ...current,
                popupHeight,
                popupWidth,
                popupWidgets
            };
        });
    }, [props.height, props.width, widgetsSignature, getDefaultWidgets]);

    useEffect(() => {
        if (popup.current && popup.current.instance) {
            popup.current.instance.option('height', mainState.popupHeight);
            popup.current.instance.option('width', mainState.popupWidth);
        }
    }, [mainState.popupHeight, mainState.popupWidth]);

    useEffect(() => {
        return () => {
            delete popup.current;
            popup.current = null;
        };
    }, []);

    useImperativeHandle(ref, () => ({
        show: show,
        close: close,
        setHeight: setHeight,
        setWidth: setWidth,
        setSize: setSize,
        isShowing: isShowing
    }));

    const popupClassName = useMemo(() => {
        let classNames = ['ant-popup'];
        if (props.isGray) classNames.push('ant-popup-gray');
        if (props.autoHeight) classNames.push('ant-popup-auto-height');
        if (props.autoWidth) classNames.push('ant-popup-auto-width');
        return classNames.join(' ');
    }, []);

    return (
        <React.Fragment>
            <Popup
                    fullScreen={Boolean(props.fullScreen)}
                    wrapperAttr={{ class: popupClassName }}
                    ref={popup}
                    visible={mainState.popupVisible}
                    onHiding={close}
                    onShowing={onShowing}
                    onShown={onShown}
                    deferRendering={props.deferRendering !== undefined ? props.deferRendering : false}
                    dragEnabled={false}
                    hideOnOutsideClick={props.hideOnOutsideClick !== undefined ? props.hideOnOutsideClick : false}
                    resizeEnabled={props.resizeEnabled !== undefined ? props.resizeEnabled : true}
                    focusStateEnabled={false}
                    showTitle={false}
                    showCloseButton={false}
                    defaultHeight={mainState.popupHeight}
                    defaultWidth={mainState.popupWidth}
                    maxHeight={props.maxHeight}
                    minHeight={props.minHeight}
                    position={'center'}
                    animation={{
                        show: {
                            type: 'fadeIn',
                            duration: 500,
                            from: {
                                scale: 0.55
                            }
                        },
                        hide: {
                            type: 'fadeOut',
                            duration: 500,
                            to: {
                                opacity: 0,
                                scale: 0.55
                            },
                            from: {
                                opacity: 1,
                                scale: 1
                            }
                        }
                    }}
                >
                    <div className="asp-shell asp-shell--inherit-bg">
                        {(props.title || props.subtitle || props.headerExtra) && (
                            <div className="asp-shell__header">
                                <div className="asp-shell__header-row">
                                    <div className="asp-shell__header-title-wrap">
                                        {props.title && <div className="asp-shell__title">{AntLocalization.translateText(props.title)}</div>}
                                        {props.subtitle && <div className="asp-shell__subtitle">{AntLocalization.translateText(props.subtitle)}</div>}
                                    </div>
                                    {props.hideCloseButton ? null : (
                                        <Button icon="close" stylingMode="text" onClick={close} elementAttr={{ class: 'asp-shell__close-btn' }} />
                                    )}
                                </div>
                                {props.headerExtra && <div className="asp-shell__header-extra">{props.headerExtra}</div>}
                            </div>
                        )}
                        <div className="asp-shell__body asp-shell__body--legacy">
                            <div id={props.id} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                {mainState.popupVisible && props.children}
                            </div>
                        </div>
                        <div className="asp-shell__footer">
                            {props.errorText && (
                                <AntErrorPanel errors={props.errors} id={props.name} text={AntLocalization.translateText(props.errorText)} />
                            )}
                            {/* <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} /> */}
                            <Toolbar className="ant-bottom-bar">
                                {/* {!separateErrorLine && props.errorText && (
                        <Item location={'before'}>
                            <AntErrorPanel errors={props.errors} id={props.name} text={AntLocalization.translateText(props.errorText)} />
                        </Item>
                    )} */}
                                {mainState.popupWidgets.map((item, i) => {
                                    let isProcessing = mainState.processingIndex === item.index;

                                    if (!item.type || item.type == 'button')
                                        return (
                                            <Item key={i} location={item.location ? item.location : 'after'}>
                                                <Button
                                                    id={'ant-popup-button-id-' + item.index}
                                                    onClick={onAction}
                                                    type={item.buttonType ? item.buttonType : 'normal'}
                                                    className="ant-processing-button"
                                                    width={item.width ? item.width : 130}
                                                    disabled={isProcessing || item.disabled === true}
                                                >
                                                    <LoadIndicator
                                                        className="ant-indicator"
                                                        visible={isProcessing}
                                                        // style={{
                                                        //     display: !isProcessing ? 'none' : 'inline-block'
                                                        // }}
                                                    />

                                                    <i
                                                        className={'dx-icon dx-icon-' + item.icon}
                                                        style={{
                                                            marginRight: 9,
                                                            display: isProcessing ? 'none' : 'inline-block'
                                                        }}
                                                    ></i>

                                                    <span
                                                        className="dx-button-text"
                                                        style={{
                                                            marginLeft: isProcessing && item.waiting_text ? 7 : 0
                                                        }}
                                                    >
                                                        {AntLocalization.translateText(
                                                            isProcessing
                                                                ? item.waiting_text
                                                                    ? item.waiting_text
                                                                    : { vi: 'Chờ xử lý', en: 'Waiting...', fr: 'En attente', es: 'Pendiente', id: 'Tertunda', ja: '保留中', zh: '待处理', ko: '보류 중', hi: 'लंबित', ar: 'معلق' }
                                                                : item.text
                                                        )}
                                                    </span>
                                                </Button>
                                            </Item>
                                        );
                                    else if (item.type == 'label-only')
                                        return (
                                            <Item location={item.location ? item.location : 'before'}>
                                                <span>{AntLocalization.translateText(item.text)}</span>
                                            </Item>
                                        );
                                })}

                                <Item
                                    location="after"
                                    widget="dxButton"
                                    className="ant-processing-button"
                                    options={{
                                        icon: 'spinright',
                                        text: AntLocalization.translateText(props.closeText ? props.closeText : { vi: 'Đóng', en: 'Close', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' }),
                                        width: props.closeWidth ? props.closeWidth : 130,
                                        type: 'normal',
                                        onClick: close
                                    }}
                                />
                            </Toolbar>
                        </div>
                    </div>
            </Popup>
        </React.Fragment>
    );
});
